import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, Html, useProgress } from '@react-three/drei';
import { Box, Typography, CircularProgress, IconButton, Fab } from '@mui/material';
import { 
  Fullscreen, 
  ZoomIn, 
  ZoomOut, 
  RotateLeft, 
  RotateRight,
  PlayArrow,
  Pause,
  Settings
} from '@mui/icons-material';

interface AssetData {
  id: string;
  name: string;
  type: string;
  status: 'healthy' | 'warning' | 'critical';
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  health: number;
  temperature: number;
  vibration: number;
  pressure: number;
}

interface ThreeJSAssetViewerProps {
  assets: AssetData[];
  selectedAsset?: string;
  onAssetSelect: (assetId: string) => void;
  isMobile: boolean;
}

// Loading component
function Loader() {
  const { progress } = useProgress();
  return (
    <Html center>
      <Box sx={{ textAlign: 'center', color: 'white' }}>
        <CircularProgress size={60} sx={{ color: '#007AFF', mb: 2 }} />
        <Typography variant="body2">
          Loading 3D Assets... {Math.round(progress)}%
        </Typography>
      </Box>
    </Html>
  );
}

// 3D Asset component
function AssetModel({ asset, isSelected, onSelect }: { 
  asset: AssetData; 
  isSelected: boolean; 
  onSelect: () => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (meshRef.current) {
      // Gentle rotation animation
      meshRef.current.rotation.y += 0.005;
      
      // Pulsing effect for critical assets
      if (asset.status === 'critical') {
        const scale = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.1;
        meshRef.current.scale.setScalar(scale);
      }
    }
  });

  const getColor = () => {
    if (isSelected) return '#007AFF';
    if (hovered) return '#34C759';
    switch (asset.status) {
      case 'healthy': return '#34C759';
      case 'warning': return '#FF9500';
      case 'critical': return '#FF3B30';
      default: return '#8E8E93';
    }
  };

  const getGeometry = () => {
    switch (asset.type) {
      case 'pump': return <cylinderGeometry args={[0.5, 0.5, 1, 8]} />;
      case 'motor': return <boxGeometry args={[1, 0.8, 0.6]} />;
      case 'valve': return <sphereGeometry args={[0.4, 16, 16]} />;
      case 'sensor': return <octahedronGeometry args={[0.3]} />;
      case 'tank': return <cylinderGeometry args={[0.8, 0.8, 1.5, 16]} />;
      default: return <boxGeometry args={[0.8, 0.8, 0.8]} />;
    }
  };

  return (
    <mesh
      ref={meshRef}
      position={asset.position}
      rotation={asset.rotation}
      scale={asset.scale}
      onClick={onSelect}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      {getGeometry()}
      <meshStandardMaterial 
        color={getColor()} 
        emissive={asset.status === 'critical' ? '#FF3B30' : '#000000'}
        emissiveIntensity={asset.status === 'critical' ? 0.3 : 0}
        metalness={0.7}
        roughness={0.3}
      />
      
      {/* Asset label */}
      <Html distanceFactor={10}>
        <Box
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
            textAlign: 'center',
            minWidth: 80,
            transform: 'translateY(-20px)',
          }}
        >
          {asset.name}
          <Box sx={{ fontSize: '0.6rem', color: getColor(), mt: 0.5 }}>
            {asset.status.toUpperCase()}
          </Box>
        </Box>
      </Html>
    </mesh>
  );
}

// Scene component
function Scene({ assets, selectedAsset, onAssetSelect }: {
  assets: AssetData[];
  selectedAsset?: string;
  onAssetSelect: (assetId: string) => void;
}) {
  return (
    <>
      <Environment preset="warehouse" />
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight position={[-10, -10, -5]} intensity={0.5} />
      
      {assets.map((asset) => (
        <AssetModel
          key={asset.id}
          asset={asset}
          isSelected={selectedAsset === asset.id}
          onSelect={() => onAssetSelect(asset.id)}
        />
      ))}
    </>
  );
}

// Main ThreeJS Asset Viewer Component
const ThreeJSAssetViewer: React.FC<ThreeJSAssetViewerProps> = ({
  assets,
  selectedAsset,
  onAssetSelect,
  isMobile,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showControls, setShowControls] = useState(!isMobile);

  const canvasRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      canvasRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const selectedAssetData = assets.find(asset => asset.id === selectedAsset);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: isMobile ? '60vh' : '70vh',
        bgcolor: '#000000',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* 3D Canvas */}
      <Canvas
        camera={{ position: [5, 5, 5], fov: 50 }}
        style={{ width: '100%', height: '100%' }}
      >
        <Scene
          assets={assets}
          selectedAsset={selectedAsset}
          onAssetSelect={onAssetSelect}
        />
        <OrbitControls
          enablePan={!isMobile}
          enableZoom={true}
          enableRotate={true}
          autoRotate={isPlaying}
          autoRotateSpeed={0.5}
        />
        <Loader />
      </Canvas>

      {/* Control Panel */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={() => setIsPlaying(!isPlaying)}
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.9)' },
          }}
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>
        
        <IconButton
          onClick={toggleFullscreen}
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.9)' },
          }}
        >
          <Fullscreen />
        </IconButton>
      </Box>

      {/* Asset Information Panel */}
      {selectedAssetData && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            right: 16,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            p: 2,
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {selectedAssetData.name}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: selectedAssetData.status === 'healthy' ? '#34C759' : 
                           selectedAssetData.status === 'warning' ? '#FF9500' : '#FF3B30',
                }}
              />
              <Typography variant="body2">
                {selectedAssetData.status.toUpperCase()}
              </Typography>
            </Box>
            
            <Typography variant="body2" color="#a1a1aa">
              Health: {selectedAssetData.health}%
            </Typography>
            <Typography variant="body2" color="#a1a1aa">
              Temp: {selectedAssetData.temperature}°C
            </Typography>
            <Typography variant="body2" color="#a1a1aa">
              Vib: {selectedAssetData.vibration}Hz
            </Typography>
          </Box>
        </Box>
      )}

      {/* Mobile Touch Controls */}
      {isMobile && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Fab
            size="small"
            onClick={() => setShowControls(!showControls)}
            sx={{
              bgcolor: 'rgba(0, 122, 255, 0.8)',
              color: 'white',
              '&:hover': { bgcolor: '#007AFF' },
            }}
          >
            <Settings />
          </Fab>
        </Box>
      )}

      {/* Asset List Overlay (Mobile) */}
      {isMobile && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            p: 2,
            maxHeight: '40%',
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
            Assets ({assets.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {assets.map((asset) => (
              <Box
                key={asset.id}
                onClick={() => onAssetSelect(asset.id)}
                sx={{
                  bgcolor: selectedAsset === asset.id ? '#007AFF' : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  border: `1px solid ${
                    asset.status === 'healthy' ? '#34C759' : 
                    asset.status === 'warning' ? '#FF9500' : '#FF3B30'
                  }`,
                }}
              >
                {asset.name}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ThreeJSAssetViewer;
